import { Input } from "@/lib/components/Input";
import { SceneTypeDto, TextElementDto } from "@repo/api/overlays";
import { ElementPositionInput } from "./ElementSliderInput";
import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import ElementAppearanceSection from "./ElementAppearanceSection";
import { ColorPicker } from "@/lib/components/ColorPicker";
import ElementFontSection from "./ElementFontSection";
import ElementShadowSection from "./ElementShadowSection";
import ElementTransitionSection from "./Transition/ElementTransitionSection";
import { CompositionTimings } from "@repo/video-engine-i2v/utils/timing";
import { getResolution } from "@repo/api/utils/composition";

type Props = {
  element: TextElementDto;
  sceneType: SceneTypeDto;
  compositionTimings: CompositionTimings;
};

export default function TextElementEditor({
  element,
  sceneType,
  compositionTimings,
}: Props) {
  const composition = useEditorStore((state) => state.video!.compositionJson);
  const updateElement = useEditorStore((state) => state.service.updateElement);

  const resolution = getResolution(composition.aspectRatio);

  return (
    <div className="px-6">
      <div className="input-label">Text</div>
      <Input
        value={element.text}
        placeholder="Write your text here"
        onChange={(e) => {
          updateElement(element.externalId, (element) => {
            if (element.type !== "TEXT") {
              throw new Error("Element is not a text element");
            }

            element.text = e.target.value;
          });
        }}
      />
      <div className="input-label mt-8">Position</div>
      <div className="flex gap-4">
        <ElementPositionInput
          fieldName="x"
          element={element}
          sceneType={sceneType}
          sliderMax={resolution.width}
        />
        <ElementPositionInput
          fieldName="y"
          element={element}
          sceneType={sceneType}
          sliderMax={resolution.height}
        />
      </div>

      <div className="input-label mt-8">Typography</div>
      <ElementFontSection element={element} sceneType={sceneType} />
      <ElementAppearanceSection element={element} sceneType={sceneType} />
      <div className="input-label mt-8">Color</div>
      <ColorPicker
        className="w-74"
        value={element.color.color}
        opacity={element.color.opacity ?? element.opacity ?? 100}
        onChange={(color) => {
          updateElement(element.externalId, (element) => {
            if (!("color" in element)) {
              throw new Error("Color is not in element");
            }
            element.color.color = color;
          });
        }}
        onOpacityChange={(opacity) => {
          updateElement(element.externalId, (element) => {
            if (!("color" in element)) {
              throw new Error("Color is not in element");
            }
            element.color.opacity = opacity;
          });
        }}
      />
      <ElementShadowSection element={element} sceneType={sceneType} />
      <ElementTransitionSection
        element={element}
        fieldName="enterAt"
        sceneType={sceneType}
        compositionTimings={compositionTimings}
      />
      <ElementTransitionSection
        element={element}
        fieldName="exitAt"
        sceneType={sceneType}
        compositionTimings={compositionTimings}
      />
    </div>
  );
}
